/**
 * Barra de filtros de los listados de la tienda: resultados de busqueda,
 * categoria y linea.
 *
 * Con cientos de resultados ("fresa" da casi 800) bajar tarjeta por tarjeta
 * no es buscar. Es la misma barra de la tabla de productos del backoffice
 * —mismas piezas, mismas clases—, pero las opciones salen de los resultados y
 * no del catalogo entero, cada una con cuantos productos tiene: buscando
 * "fresa" no se ofrece "Bisagras", y se ve de antemano cuanto deja cada filtro.
 *
 * Todos se combinan entre si. Categoria y marca abren una lista con buscador,
 * porque pueden ser decenas; el control que esta filtrando se pinta en azul.
 */
import { useState } from 'react';

import type { FacetasBusqueda, FiltrosBusqueda, OrdenCatalogo } from '@elcarpintero/compartido';

import { conCambio, ORDENES_BUSQUEDA } from '../../utilidades/filtros-busqueda';
import { numero, pesos } from '../../utilidades/formato';
import { FiltroMonto } from '../administracion/FiltroMonto';
import { Icono } from '../comunes/Icono';
import { SelectorOpciones } from '../comunes/SelectorOpciones';

export interface FiltrosBusquedaProps {
  filtros: FiltrosBusqueda;
  facetas: FacetasBusqueda;
  onCambiar(filtros: FiltrosBusqueda): void;
}

interface BotonListaProps {
  id: string;
  etiqueta: string;
  elegidas: number;
  /** Cuantas opciones hay para elegir; sin ninguna, el boton no sirve. */
  disponibles: number;
  onAbrir(): void;
}

function BotonLista({ id, etiqueta, elegidas, disponibles, onAbrir }: BotonListaProps) {
  return (
    <div className="filtro">
      <span className="filtro-et" id={`${id}-et`}>
        {etiqueta}
      </span>
      <button
        type="button"
        className={elegidas > 0 ? 'btn-filtro activo' : 'btn-filtro'}
        aria-labelledby={`${id}-et ${id}-valor`}
        disabled={disponibles === 0 && elegidas === 0}
        onClick={onAbrir}
      >
        <span id={`${id}-valor`}>
          {elegidas > 0 ? `${elegidas} elegidas` : `Todas (${numero(disponibles)})`}
        </span>
        <Icono nombre="chevron" className="gira-abajo" />
      </button>
    </div>
  );
}

export function FiltrosBusqueda({ filtros, facetas, onCambiar }: FiltrosBusquedaProps) {
  const [selector, setSelector] = useState<'categorias' | 'marcas' | null>(null);

  const cambiar = (cambio: Partial<FiltrosBusqueda>) => {
    onCambiar(conCambio(filtros, cambio));
  };

  return (
    <section className="filtros-productos filtros-busqueda" aria-label="Filtrar resultados">
      {/* Con una sola categoria (la vista de una categoria, o una busqueda
          que cae toda en una) no hay nada que elegir. Si ya estaba filtrando
          se deja, para poder sacarlo. */}
      {facetas.categorias.length > 1 || filtros.categorias !== undefined ? (
        <BotonLista
          id="filtro-busqueda-categoria"
          etiqueta="Categoría"
          elegidas={filtros.categorias?.length ?? 0}
          disponibles={facetas.categorias.length}
          onAbrir={() => {
            setSelector('categorias');
          }}
        />
      ) : null}

      <BotonLista
        id="filtro-busqueda-marca"
        etiqueta="Marca"
        elegidas={filtros.marcas?.length ?? 0}
        disponibles={facetas.marcas.length}
        onAbrir={() => {
          setSelector('marcas');
        }}
      />

      <FiltroMonto
        que="Precio"
        minimo={filtros.precioMin}
        maximo={filtros.precioMax}
        // Los extremos de los resultados como ejemplo: dicen de un vistazo
        // entre que precios se mueve lo encontrado.
        {...(facetas.precio !== null
          ? { marcadores: { desde: pesos(facetas.precio.minimo), hasta: pesos(facetas.precio.maximo) } }
          : {})}
        onCambiar={({ minimo, maximo }) => {
          cambiar({ precioMin: minimo, precioMax: maximo });
        }}
      />

      <label className="opcion-filtro filtro-casilla">
        <input
          type="checkbox"
          checked={filtros.conStock === true}
          onChange={(evento) => {
            cambiar({ conStock: evento.target.checked ? true : undefined });
          }}
        />
        Con stock ({numero(facetas.conStock)})
      </label>

      {/* Sin ofertas entre los resultados la casilla no ofrece nada; si ya
          estaba marcada se deja, para poder desmarcarla. */}
      {facetas.enOferta > 0 || filtros.enOferta === true ? (
        <label className="opcion-filtro filtro-casilla">
          <input
            type="checkbox"
            checked={filtros.enOferta === true}
            onChange={(evento) => {
              cambiar({ enOferta: evento.target.checked ? true : undefined });
            }}
          />
          En oferta ({numero(facetas.enOferta)})
        </label>
      ) : null}

      <div className="filtro filtro-orden">
        <label className="filtro-et" htmlFor="filtro-busqueda-orden">
          Ordenar por
        </label>
        <select
          id="filtro-busqueda-orden"
          className="selector"
          value={filtros.orden ?? 'relevancia'}
          onChange={(evento) => {
            const orden = evento.target.value as OrdenCatalogo;
            cambiar({ orden: orden === 'relevancia' ? undefined : orden });
          }}
        >
          {ORDENES_BUSQUEDA.map((opcion) => (
            <option key={opcion.valor} value={opcion.valor}>
              {opcion.texto}
            </option>
          ))}
        </select>
      </div>

      {selector === 'categorias' ? (
        <SelectorOpciones
          titulo="Filtrar por categoría"
          que="categoria"
          opciones={facetas.categorias}
          elegidas={filtros.categorias ?? []}
          onCerrar={() => {
            setSelector(null);
          }}
          onAplicar={(categorias) => {
            setSelector(null);
            cambiar({ categorias });
          }}
        />
      ) : null}

      {selector === 'marcas' ? (
        <SelectorOpciones
          titulo="Filtrar por marca"
          que="marca"
          opciones={facetas.marcas}
          elegidas={filtros.marcas ?? []}
          onCerrar={() => {
            setSelector(null);
          }}
          onAplicar={(marcas) => {
            setSelector(null);
            cambiar({ marcas });
          }}
        />
      ) : null}
    </section>
  );
}
